// Bailey's reply, tidied for a quick read on a phone.
//
// Ben, 6 Oct 2026: replies were text heavy, asked "would you like me to do A,
// B or C?" in words they had to type an answer to, and told them where to
// open a draft instead of showing it. The briefing now asks the model for
// short replies and tap-to-answer choices (context.js STYLE), and this is the
// app's side, for when it doesn't:
//  - "I'll check what campaigns you've made." lines before the answer are
//    dropped (the activity line already said what Bailey was doing);
//  - with the draft on screen as a card, "Go to Marketing › Campaigns and tap
//    the draft" sentences are dropped (the card opens it);
//  - a closing question with two or three short answers, or a yes/no
//    ("Shall I…?"), gets buttons; an "If you'd rather X or Y, just say" line
//    becomes buttons instead of words.
// No regex lookbehind anywhere: it breaks the whole bundle on older iOS.
import { splitReply } from './protocol'

// "I'll check what campaigns you've made." / "Let me draft a campaign for new
// clients using the welcome template." (a sentence about what's coming next)
const NARRATION = /^\s*(?:ok(?:ay)?[,.!]?\s+|sure[,.!]?\s+|right[,.!]?\s+)?(?:i['’]ll|i will|let me|let['’]s|i['’]m going to|i am going to|first,? i['’]ll|first,? let me)\s+(?:now\s+|quickly\s+|just\s+)?(?:check|look|see|find|pull|search|review|go through|get|have a look|take a look|draft|create|make|write|put together|set up|build|show|pull up|bring up|walk you through|gather|go over|run through)\b[^.!?]*[.!…]*\s*$/i
const OPEN_HOW = /\b(?:go to|head to|head over to|open it (?:in|from)|open the draft|tap the draft|tap on the draft|find it (?:in|under)|you['’]ll find it|you['’]ll need to open|you will need to open|to finish it|from there)\b/i
const YES_NO = /^(?:so,?\s+)?(?:would you like(?: me)? to|do you want(?: me)? to|shall i|should i|want me to|can i|would it help if i)\s+/i
const RATHER = /^if you(?:['’]d| would) (?:rather|prefer|like)(?: to)?\s+(.+?),?\s+(?:just\s+)?(?:let me know|say so|say|tell me|ask)\b[.!]*$/i

// "I'd suggest X, or Y." (a suggestion with no question: still a choice)
const SUGGEST = /^\s*(?:so,?\s+)?(?:i['’]d (?:suggest|recommend)|i would (?:suggest|recommend)|i (?:suggest|recommend)|you could|you might|my (?:suggestion|pick) (?:is|would be)|the best (?:bet|move) (?:is|would be)|try)\s+/i
// "Bailey has ready-made ideas for this week too." (it meant to show them)
const READY = /\bready[- ]made (?:ideas|plans|campaigns)\b/i
const READY_LABEL = 'Show the ready-made ideas'
// "…to fill those slots" after a suggestion: why, not what
const PURPOSE = /\s+(?:(?:so as )?to\s+(?:fill|get|bring|boost|show|showcase|win|keep|drive|attract|help|reach|grab|catch|encourage|remind|nudge|tempt)\b|so that\b|so they\b|which\b|and then\b).*$/i
const GERUND = { running: 'Run', promoting: 'Promote', making: 'Make', sending: 'Send', writing: 'Write', posting: 'Post', sharing: 'Share', creating: 'Create', offering: 'Offer', planning: 'Plan', launching: 'Launch', showcasing: 'Showcase', setting: 'Set', adding: 'Add', using: 'Use', giving: 'Give', putting: 'Put', doing: 'Do', starting: 'Start', building: 'Build' }
const asAction = (s) => {
  const t = s.trim().replace(/[.,;:!?]+$/, '')
  const m = /^(\w+)(\b.*)$/.exec(t)
  if (m && GERUND[m[1].toLowerCase()]) return GERUND[m[1].toLowerCase()] + m[2]
  return t.charAt(0).toUpperCase() + t.slice(1)
}

// The choices in a suggestion: its bold phrases, or its "X, or Y" parts
// without the "to fill those slots" that follows each
const suggestedIn = (sentence) => {
  if (!SUGGEST.test(plainOf(sentence))) return null
  const bold = []
  const re = /\*\*([^*]{2,60})\*\*/g
  let m = re.exec(sentence)
  while (m) { bold.push(m[1]); m = re.exec(sentence) }
  let opts = bold.length >= 2 ? bold : null
  if (!opts) {
    const rest = plainOf(sentence).replace(SUGGEST, '')
    if (!/\bor\b/i.test(rest)) return null
    opts = rest.split(/,\s*(?:or\s+)?|\s+or\s+/i).map(o => o.replace(PURPOSE, ''))
  }
  opts = opts.map(asAction).filter(o => o && o.length <= 48 && words(o) <= 8)
  return opts.length >= 2 && opts.length <= 3 ? opts : null
}

const INLINE_CHOICES = /\{\{\s*choices?\s*:[\s\S]*?\]\s*\}\}/gi

// A sentence ends at . ! or ? followed by a space or the end, so money
// ("£28,677.20"), times ("10.30") and links ("beu.app/book") stay whole
// (Haiku 5.5 writes pence, 8 Oct 2026, and the old rule split "£28,677." from
// "20" and broke the bold round it)
const sentencesOf = (text) => String(text || '').match(/(?:[^.!?]|[.!?](?![\s"”’)]|$))+(?:[.!?]+["”’)]*)?\s*/g) || []
const words = (s) => s.trim().split(/\s+/).filter(Boolean).length
const upFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
const plainOf = (s) => String(s || '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[*_`]/g, '').trim()

// "send a text instead, make a post or something else" -> up to 3 labels
const optionsIn = (s, maxWords) => {
  const body = plainOf(s).replace(/[?.!]+$/, '').replace(/\s+instead$/i, '')
  if (!/\bor\b/i.test(body)) return null
  const opts = body.split(/,\s*(?:or\s+)?|\s+or\s+/i).map(o => o.trim().replace(/^(?:to|and)\s+/i, '').replace(/\s+instead$/i, '')).filter(Boolean)
  if (opts.length < 2 || opts.length > 3) return null
  if (opts.some(o => words(o) > maxWords || o.length > 48 || /\bsomething else\b/i.test(o))) return null
  return opts.map(upFirst)
}

// Two or three things named in quotes ("October win-back" and "Lapsed 3+
// months"), for a "Which would help?" that names its answers earlier on
const quotedIn = (text) => {
  const found = []
  const re = /["“]([^"“”\n]{2,48})["”]/g
  let m = re.exec(String(text || ''))
  while (m) {
    const q = m[1].trim()
    if (q && found.indexOf(q) === -1) found.push(q)
    m = re.exec(String(text || ''))
  }
  return found.length >= 2 && found.length <= 3 ? found.map(upFirst) : null
}

// The answers a closing question offers, or null. `before` is what was said
// before it in the same paragraph.
export const answersFor = (sentence, before = '') => {
  const s = plainOf(sentence)
  if (!/\?\s*$/.test(s)) return null
  if (/^(?:so,?\s+)?which\b/i.test(s) && !/\bor\b/i.test(s)) return quotedIn(before)
  const lead = YES_NO.exec(s)
  if (lead) {
    const rest = s.slice(lead[0].length)
    return optionsIn(rest, 7) || (/\bor\b/i.test(rest) ? null : ['Yes please', 'No thanks'])
  }
  // "Email or text?" with no lead: only very short answers
  return optionsIn(s, 4)
}

const asChoices = (labels) => ({
  kind: 'block',
  block: { show: 'choices', items: labels.map(label => ({ label, say: label })) },
  raw: `auto-choices:${labels.join('|')}`,
})

// ─── Easy to scan (Ben, 6 Oct 2026: "mini paragraphs", bold what matters) ───
// Only for what's drawn in the chat (shape: true), never for previews.

// Counts, prices and percentages worth seeing at a glance
const KEY_FACT = /(£\s?\d[\d,]*(?:\.\d+)?|\b\d[\d,]*(?:\.\d+)?%|\b\d[\d,]*\+?\s+(?:(?:new|lapsed|overdue|regular|loyal|returning|first[- ]time|quiet|free|empty|open)\s+)?(?:clients?|people|bookings?|appointments?|regulars?|emails?|texts?|slots?|posts?|no-shows?|days?|weeks?|months?|hours?|minutes?|stylists?|services?|reviews?))(?![\w*])/gi
// "October win-back" -> "**October win-back**"
const QUOTED = /(["“])([^"“”\n]{2,60})(["”])/g

const boldFacts = (text) => {
  if (/\*\*/.test(text)) return text // the model chose its own emphasis
  // Leave links alone: [words](/path)
  return text.split(/(\[[^\]]*\]\([^)]*\))/).map((seg, i) => (i % 2 === 1 ? seg
    : seg.replace(QUOTED, (m, a, q, b) => `${a}**${q.trim()}**${b}`).replace(KEY_FACT, m => `**${m.trim()}**`))).join('')
}

// A paragraph of two or more sentences as mini paragraphs of one or two,
// with a closing question on its own
const miniParas = (para) => {
  const sentences = sentencesOf(para).map(x => x.trim()).filter(Boolean)
  if (sentences.length < 2) return [para]
  const question = /\?["”’)]*$/.test(sentences[sentences.length - 1]) ? sentences.pop() : null
  const out = []
  let cur = []
  sentences.forEach((x) => {
    cur.push(x)
    // Two short sentences together; a long one stands on its own
    if (cur.length === 2 || cur.join(' ').length > 110) { out.push(cur.join(' ')); cur = [] }
  })
  if (cur.length) out.push(cur.join(' '))
  if (question) out.push(question)
  return out
}

// No dashes as punctuation in what clients and owners read (Beu's copy rule);
// the model is told so, and this catches the ones that slip through
const noDashes = (text) => text.replace(/\s+[—–]\s+/g, ', ').replace(/([^\s\d])[—]([^\s\d])/g, '$1, $2')

const shapeText = (text) => noDashes(text).split(/\n{2,}/).map((para) => {
  if (/^\s*(?:[-*+]\s|\d+[.)]\s|#|>|\|)/m.test(para)) return para // lists, headings, tables
  return miniParas(para.trim()).map(boldFacts).join('\n\n')
}).join('\n\n')

/**
 * splitReply(text) parts, tidied. `drafts` are the campaign drafts shown as
 * cards beside this reply.
 */
export const tidyParts = (parts, opts = {}) => {
  // Narration goes only while something else is still said
  const tidied = tidyOnce(parts, { ...opts, dropNarration: true })
  const shown = Array.isArray(opts.drafts) && opts.drafts.length > 0
  const out = shown || tidied.some(p => p.kind === 'md' || p.kind === 'block' || p.kind === 'pending') ? tidied : tidyOnce(parts, opts)
  return opts.shape ? out.map(p => (p.kind === 'md' ? { ...p, text: shapeText(p.text) } : p)) : out
}

const tidyOnce = (parts, { drafts = null, dropNarration = false } = {}) => {
  const list = Array.isArray(parts) ? parts : []
  const hasDraft = Array.isArray(drafts) && drafts.length > 0
  const hasChoices = list.some(p => p.kind === 'block' && p.block && p.block.show === 'choices')
  const streaming = list.some(p => p.kind === 'pending')
  let offered = null
  let ready = false
  const out = []
  list.forEach((p) => {
    if (p.kind !== 'md') { out.push(p); return }
    // Choices written as text ({{choices:items:[{label:"..."}]}}, seen on
    // staging 6 Oct 2026) become the buttons they were meant to be
    let text = p.text.replace(INLINE_CHOICES, (m) => {
      const labels = []
      const re = /label\s*:\s*["“]([^"”]{1,48})["”]/g
      let x = re.exec(m)
      while (x) { labels.push(x[1].trim()); x = re.exec(m) }
      if (!hasChoices && !offered && labels.length >= 2) offered = labels.slice(0, 3)
      return ''
    })
    // "**Email draft ready. **": bold the model broke with a space
    text = text.replace(/\*\*[ \t]*([^*\n]+?)[ \t]*\*\*/g, '**$1**')
    const paras = text.split(/\n{2,}/)
    const kept = []
    paras.forEach((para) => {
      const t = para.trim()
      if (!t) return
      // Lists, headings and quotes stay as they are
      if (/^\s*(?:[-*+]\s|\d+[.)]\s|#|>)/m.test(t)) { kept.push(para); return }
      let line = t
      if (hasDraft) line = sentencesOf(line).filter(s => !OPEN_HOW.test(s)).join('').trim()
      if (dropNarration) line = sentencesOf(line).filter(s => !NARRATION.test(s)).join('').trim()
      // "There are ready-made ideas too": a button, not a line
      if (!hasChoices && READY.test(line)) {
        const rest = sentencesOf(line).filter(s => !READY.test(s))
        if (rest.length < sentencesOf(line).length) { ready = true; line = rest.join('').trim() }
      }
      const rather = !hasChoices && RATHER.exec(plainOf(line))
      if (rather) {
        const opts = optionsIn(rather[1], 7)
        if (opts && !offered) { offered = opts; return }
      }
      if (line) kept.push(line)
    })
    if (kept.length) out.push({ ...p, text: kept.join('\n\n') })
  })
  if (hasChoices || streaming) return out
  if (!offered) {
    // The last thing said, if it's a question with answers to tap
    const last = [...out].reverse().find(p => p.kind === 'md')
    if (last) {
      const sentences = sentencesOf(last.text.split(/\n{2,}/).pop())
      const q = sentences.length ? sentences[sentences.length - 1] : ''
      offered = answersFor(q, sentences.slice(0, -1).join(''))
    }
  }
  if (!offered) {
    // A suggestion of two or three things to do, anywhere in the reply
    out.filter(p => p.kind === 'md').some(p => sentencesOf(p.text).some((x) => {
      const got = suggestedIn(x)
      if (got) offered = got
      return !!got
    }))
  }
  if (ready) offered = [...(offered || []).slice(0, 2), READY_LABEL]
  if (offered && offered.length < 2 && !ready) offered = null
  if (offered && offered.length === 1) offered = [offered[0], 'Not now']
  if (offered) out.push(asChoices(offered))
  return out
}

/** Does this reply ask them something with answers to tap? */
export const asksToTap = (parts) => tidyParts(parts).some(p => p.kind === 'block' && p.block && p.block.show === 'choices')

/** A reply's words as tidied (for the line beside the desk pet and previews) */
export const tidyWords = (text, opts) => tidyParts(splitReply(text), opts)
  .filter(p => p.kind === 'md').map(p => p.text.trim()).filter(Boolean).join('\n\n')
